import { api } from './client';

// ── Types ──

export interface TemplateVariable {
  name: string;
  description: string;
  example: string;
}

export interface Prompt {
  id: number;
  step_number: number;
  name: string;
  description: string | null;
  system_prompt: string;
  user_prompt_template: string;
  template_variables: TemplateVariable[];
  output_format: 'markdown' | 'json' | 'xml';
  output_tag: string | null;
  ai_model: string;
  version: number;
  is_active: boolean;
  ab_group: 'A' | 'B' | null;
  usage_count: number;
  avg_quality_score: number | null;
  avg_duration_ms: number | null;
  avg_cost: number | null;
  created_by: string;
  updated_by: string;
  created_at: string;
  updated_at: string;
}

export interface CreatePromptPayload {
  step_number: number;
  name: string;
  system_prompt: string;
  user_prompt_template: string;
  output_format?: string;
  output_tag?: string;
  ai_model?: string;
  description?: string;
}

export interface UpdatePromptPayload {
  name?: string;
  system_prompt?: string;
  user_prompt_template?: string;
  output_format?: string;
  output_tag?: string;
  ai_model?: string;
  is_active?: boolean;
  description?: string;
}

export interface PromptRun {
  id: number;
  report_id: string;
  request_id: string;
  step_number: number;
  step_name: string;
  status: string;
  ai_model: string;
  prompt_version: number;
  prompt_id: number | null;
  system_prompt: string | null;
  user_prompt: string | null;
  output_markdown: string | null;
  output_json: Record<string, unknown> | null;
  output_raw: string | null;
  tokens_input: number | null;
  tokens_output: number | null;
  cost: number | null;
  duration_ms: number | null;
  error_message: string | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  keyword: string | null;
}

// ── API ──

export const promptApi = {
  /** List all prompts (optionally filter by step and active status) */
  list: (filters?: { step?: number; active?: boolean }) => {
    const params = new URLSearchParams();
    if (filters?.step) params.set('step', String(filters.step));
    if (filters?.active) params.set('active', 'true');
    const qs = params.toString();
    return api.get<Prompt[]>(`/prompts${qs ? `?${qs}` : ''}`);
  },

  /** Get single prompt by ID */
  get: (id: number) =>
    api.get<Prompt>(`/prompts/${id}`),

  /** Create new prompt version */
  create: (payload: CreatePromptPayload) =>
    api.post<Prompt>('/prompts', payload),

  /** Update existing prompt */
  update: (id: number, payload: UpdatePromptPayload) =>
    apiFetchPut<Prompt>(`/prompts/${id}`, payload),

  /** Deactivate prompt */
  deactivate: (id: number) =>
    api.delete(`/prompts/${id}`),

  /** Get execution history (real input + output) for a step */
  runs: (stepNumber: number, limit = 20, offset = 0) =>
    api.get<{ runs: PromptRun[]; total: number }>(
      `/prompts/runs/${stepNumber}?limit=${limit}&offset=${offset}`
    ),
};

// api.ts doesn't have a put method, add inline
import { apiFetch } from './client';

function apiFetchPut<T>(path: string, body: unknown) {
  return apiFetch<T>(path, { method: 'PUT', body: JSON.stringify(body) });
}
